import { GAME_KINDS, gameKindLabel, type GameKind, type GameMeta } from '@awwyeah/shared';

/**
 * The fact-card copy for a finished game in the check-in feed. Small and pure so the
 * "generic off `GameMeta.kind`" rule (ROADMAP: "so future games appear automatically") is
 * testable without a browser or a fixture for every kind that will ever exist.
 *
 * Duplicates `apps/web/src/modules/games/archive.ts`'s `outcomeLabel` rather than importing
 * it — feature modules don't reach into each other's internals (docs/ARCHITECTURE.md; the
 * same precedent that file's own docblock notes for the reverse direction).
 */

const KNOWN_KINDS: readonly string[] = GAME_KINDS;

/** "battle_royale" → "Battle royale" — the fallback for a kind `gameKindLabel` doesn't
 * know about yet. Never thrown: a malformed or future-shaped `kind` still renders
 * *something* legible rather than blanking the card. */
export function prettifyKind(kind: string): string {
  const spaced = kind.replace(/[_-]+/g, ' ').trim();
  if (spaced.length === 0) return 'Game';
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Display name for any `kind` string, known or not — `gameKindLabel` for the four kinds
 * it covers today, `prettifyKind` for anything else so a future kind (or a malformed doc)
 * never breaks the card. */
export function kindLabel(kind: string): string {
  if (KNOWN_KINDS.includes(kind)) return gameKindLabel(kind as GameKind);
  return prettifyKind(kind);
}

/** "Faye won" / "Draw" — a fact, never a record. `winnerName` is already resolved by the
 * caller (this module doesn't know about the roster). */
export function gameOutcomeLabel(game: GameMeta, winnerName: string | null): string {
  if (!game.winnerId) return 'Draw';
  return `${winnerName ?? 'Someone'} won`;
}
